import { FieldArray, FormikProvider, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import RenderField from "./subComponents/RenderField";
import {
  useCreateProject,
  useGetSearchData,
} from "../../../../../queries/query-hooks/workspace/workspace-hook";
import useDebounce from "../../../../../components/custom-hooks/use-debounce";
import postService from "../../../../../api/services/post-service";
import {
  ADD_PROJECT,
  GET_BANNER_GROUP,
  UPDATE_PROJECT,
} from "../../../../../api/endpoints/channel-endpoint";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECT_POST_LIST } from "../../../../../queries/query-constants/workspace/workspace-constant";
import useRedirect from "../../../../../components/custom-hooks/use-redirect";
import useBackdrop from "../../../../../components/custom-hooks/use-backdrop";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { getPrincipleEntitiesForSelect } from "../../../../../common/utils/utils";
import MappedStates from "./subComponents/MappedStates";
import MappedSpecialities from "./subComponents/MappedSpecialities";
import FormBuilder from "./subComponents/FormBuilder";
import { findIcon } from "views/dashboard/icons";

function BrandAdsForm({
  project_type_code,
  project_group_id,
  project_type_id,
  projectDetails = [],
  masterSpecialities = [],
  stateLists = [],
}) {
  const { redirectTo } = useRedirect();
  const queryClient = useQueryClient();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const [dropdownGroups, setDropdownGroups] = useState([
    { id: 1, firstSelect: "", secondSelect: "", isLocked: false },
  ]);
  const [specialitiesGroups, setSpecialitiesGroups] = useState([
    { id: 1, firstSelect: "", secondSelect: "", isLocked: false },
  ]);
  const [createdTags, setCreatedTags] = useState([]);
  const [isEditProject, setIsEditProject] = useState(false);

  useEffect(() => {
    if (projectDetails.length > 0) {
      setIsEditProject(true);
      preFillProjectForm();
    }
  }, []);

  const getMappedData = (stateData = []) => {
    const mapped_data = {};
    stateData.forEach((item) => {
      if (item?.firstSelect && item?.secondSelect) {
        mapped_data[item.firstSelect] = item.secondSelect;
      }
    });
    return mapped_data;
  };

  const formik = useFormik({
    initialValues: {
      showSelection: false,
      project_name: "",
      project_desc: "",
      principal_entity_id: "",
      project_start_date: "",
      project_end_date: "",
      project_contact_name: "",
      project_contact_email: "",
      project_contact_phone: "",
      project_po_ref: "",
      project_po_date: "",
      project_logo: "",
      project_owner: "",
      project_group_id: project_group_id,
      ga_status: 0,
      speciality_count: 0,
      project_type_id: project_type_id,
      project_impressions_target: 0,
      project_status: 1,
      project_attributes: 1,
      project_campaign_groups: [
        {
          label: "",
          group_id: "",
          group_impressions_target: 0,
          group_reach_target: 0,
        },
      ],
    },
    validationSchema: Yup.object({
      project_name: Yup.string().required("Project Name is required"),
      project_desc: Yup.string().required("Project Description is required"),
      principal_entity_id: Yup.string().required(
        "Principal Entity is required"
      ),
      project_start_date: Yup.date().required("Start date is required"),
      project_end_date: Yup.date().required("End date is required"),
      project_contact_name: Yup.string().required(
        "Project contact person name is required"
      ),
      project_contact_email: Yup.string()
        .email()
        .required("Project contact person Email is required"),
      project_contact_phone: Yup.string()
        .matches(/^[6-9]\d{9}$/, {
          message: "Please enter valid number.",
          excludeEmptyString: false,
        })
        .required("Project contact person phone is required"),
      project_po_ref: Yup.string().required("Project PO Reference is required"),
      project_po_date: Yup.string().required("Project PO Date is required"),
      project_logo: Yup.string().required("Project Logo is required"),
      ga_status: Yup.number().required("GA Status is required"),
      speciality_count: Yup.number().required("speciality count is required"),
      project_impressions_target: Yup.number()
        .required("Project Impressions Target is required")
        .moreThan(0, "Please enter valid Impression target."),

      project_campaign_groups: Yup.array()
        .required("At least one Campaign Group is required")
        .of(
          Yup.object().shape({
            label: Yup.string()
              .trim()
              .required("Campaign Group Name is required"),

            group_id: Yup.string()
              .trim()
              .required("Campaign Group ID is required"),

            group_impressions_target: Yup.number()
              .required("Campaign Group Impressions Target is required")
              .moreThan(0, "Please enter valid Impression target."),

            group_reach_target: Yup.number()
              .required("Campaign Group Reach Target is required")
              .moreThan(0, "Please enter valid Reach target."),
          })
        ),
      project_owner: Yup.string().required("Project Owner name is required"),
    }),
    onSubmit: async (values) => {
      const payload = { ...values };
      payload.project_status === 0
        ? (payload.project_status = 2)
        : (payload.project_status = 1);
      const mappedLocationData = getMappedData(dropdownGroups);
      const mappedSpecialityData = getMappedData(specialitiesGroups);
      payload.project_attributes = [
        {
          key: "demographic_by_age",
          value: values.project_attributes === 1 ? "true" : "false",
        },
        {
          key: "speciality_count",
          value: values.speciality_count || 0,
        },
        {
          key: "tags",
          value: JSON.stringify(createdTags),
        },
      ];
      if (!isEmpty(mappedLocationData)) {
        payload.project_attributes.push({
          key: "mapped_location_data",
          value: JSON.stringify(mappedLocationData),
        });
      }
      if (!isEmpty(mappedSpecialityData)) {
        payload.project_attributes.push({
          key: "mapped_speciality_data",
          value: JSON.stringify(mappedSpecialityData),
        });
      }
      showBackdrop();
      delete payload?.dbRef;
      let response;
      if (projectDetails.length > 0) {
        response = await postService(UPDATE_PROJECT, payload);
      } else {
        response = await postService(ADD_PROJECT, payload);
      }
      if (!response.data?.success) {
        toast.error("Something went wrong");
        hideBackdrop();
      } else {
        queryClient.invalidateQueries({
          queryKey: [GET_PROJECT_POST_LIST],
        });

        if (projectDetails.length > 0) {
          toast.success("Project updated successfully");
        } else {
          toast.success("Project created successfully");
        }

        hideBackdrop();
        redirectTo("workspace/clirnet");
      }
    },
  });
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;
  const { data: createProjectData, isLoading: principalEntitiesLoading } =
    useCreateProject();

  const onTagsChangeHandler = (selectedValue) => {
    setCreatedTags(selectedValue);
  };

  const fields = [
    {
      row: 1,
      columns: [
        {
          id: 1,
          label: "Project Name",
          field_name: "project_name",
          type: "text",
          placeholder: "Enter Project Name",
          required: true,
          colSize: 6,
        },
        {
          id: 2,
          label: "Principal Entity",
          field_name: "principal_entity_id",
          type: "select",
          options: getPrincipleEntitiesForSelect(
            createProjectData?.principal_entities || []
          ),
          required: true,
          optionLabelKey: "principal_entity_name",
          optionValueKey: "principal_entity_id",
          colSize: 3,
        },
        {
          id: 3,
          label: "Project Status",
          field_name: "project_status",
          type: "switch",
          required: false,
          disabled: !isEditProject,
          colSize: 3,
        },
      ],
    },
    {
      row: 2,
      columns: [
        {
          id: 1,
          label: "Project Description",
          field_name: "project_desc",
          type: "textarea",
          placeholder: "Enter Project Description",
          required: true,
        },
        {
          id: 2,
          label: "Project Logo",
          field_name: "project_logo",
          type: "file",
          required: true,
        },
      ],
    },
    {
      row: 3,
      columns: [
        {
          id: 1,
          label: "Project Owner Name",
          field_name: "project_owner",
          type: "text",
          placeholder: "Enter Project Owner",
          required: true,
        },
        {
          id: 2,
          label: "Start Date",
          field_name: "project_start_date",
          type: "date",
          required: true,
        },
        {
          id: 3,
          label: "End Date",
          field_name: "project_end_date",
          type: "date",
          required: true,
        },
      ],
    },
    {
      row: 4,
      columns: [
        {
          id: 1,
          label: "Project Contant Person Name",
          field_name: "project_contact_name",
          type: "text",
          placeholder: "Enter Owner Contact",
          required: true,
        },
        {
          id: 2,
          label: "Project Contant Person Email",
          field_name: "project_contact_email",
          type: "email",
          placeholder: "Enter Owner Email",
          required: true,
        },
        {
          id: 3,
          label: "Project Contant Person Phone",
          field_name: "project_contact_phone",
          type: "number",
          placeholder: "Enter Owner Phone",
          required: true,
        },
      ],
    },
    {
      row: 5,
      columns: [
        {
          id: 1,
          label: "Purchase Order Reference",
          field_name: "project_po_ref",
          type: "text",
          placeholder: "Enter Purchase Order Reference",
          required: true,
        },
        {
          id: 2,
          label: "Purchase Order Date",
          field_name: "project_po_date",
          type: "date",
          placeholder: "Enter Purchase Order Date",
          required: true,
        },
      ],
    },
    {
      row: 6,
      columns: [
        {
          id: 1,
          label: "Project Impressions Target",
          field_name: "project_impressions_target",
          type: "number",
          required: true,
        },
        {
          id: 2,
          label: "Speciality Count",
          field_name: "speciality_count",
          type: "number",
          placeholder: "Enter Speciality Count",
          required: true,
        },
      ],
    },
    {
      row: 7,
      columns: [
        {
          id: 1,
          label: "GA Status",
          field_name: "ga_status",
          type: "switch",
          required: false,
        },
        {
          id: 2,
          label: "Show demographic - age, gender, speaciality",
          field_name: "project_attributes",
          type: "switch",
          required: false,
        },
      ],
    },
    {
      row: 8,
      columns: [
        {
          id: 1,
          label: "Project Tags",
          field_name: "tags",
          type: "creatableSelect",
          required: false,
          options: createdTags,
          onCustomChange: onTagsChangeHandler,
        },
      ],
    },
  ];
  const [searchText, setSearchText] = useState({});
  const seachTerm = useDebounce(searchText, 1000);
  const { data: searchData, isFetching: issearchDataLoading } =
    useGetSearchData(
      {
        search_key: seachTerm,
      },
      project_type_code
    );

  const _loadSuggestions = (inputValue) => {
    return new Promise(async (resolve) => {
      const response = await postService(GET_BANNER_GROUP, {
        search_key: inputValue,
      });

      if (!response.isError) {
        const searchData = [];

        response?.data?.data.forEach((elem) => {
          searchData.push({
            value: elem.id,
            label: elem.title,
          });
        });
        resolve(searchData);
      } else {
        toast.error("Error fetching data!");
      }
    });
  };

  function preFillProjectForm() {
    const projectImpressionsTarget =
      projectDetails?.at(0)?.project_target?.impressions_target || 0;
    const projectStatus = projectDetails?.at(0)?.project_status === 2 ? 0 : 1;

    const projectCampaignGroup = projectDetails
      ?.at(0)
      ?.project_group_data?.map((elem) => {
        return {
          group_id: elem?.group_id,
          group_impressions_target: elem?.impressions_target,
          group_reach_target: elem?.reach_target,
          label: elem?.group_data?.group_title,
        };
      });

    const specialityCount = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item.attribute_name === "speciality_count"
      )?.attribute_value;

    const stateMappedAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item.attribute_name === "mapped_location_data"
      )?.attribute_value;

    const specialityMappedAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) =>
          item?.attribute_name === "mapped_speciality_data" &&
          item?.attribute_value !== "true" &&
          item?.attribute_value !== "false"
      )?.attribute_value;

    const projectTags = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "tags"
      )?.attribute_value;

    const projectTagsArray = projectTags ? JSON.parse(projectTags) : [];

    if (!!stateMappedAttribute) {
      const mappedStatesData = JSON.parse(stateMappedAttribute);

      const transformedData = Object.keys(mappedStatesData).map(
        (key, index) => ({
          id: index + 1,
          firstSelect: key,
          secondSelect: mappedStatesData[key],
          isLocked: true,
        })
      );

      transformedData.push({
        id: transformedData.length + 1,
        firstSelect: "",
        secondSelect: "",
        isLocked: false,
      });

      setDropdownGroups(transformedData);
    }

    if (!!specialityMappedAttribute) {
      const mappedSpecialityData = JSON.parse(specialityMappedAttribute);

      const transformedData = Object.keys(mappedSpecialityData).map(
        (key, index) => ({
          id: index + 1,
          firstSelect: key,
          secondSelect: mappedSpecialityData[key],
          isLocked: true,
        })
      );

      transformedData.push({
        id: transformedData.length + 1,
        firstSelect: "",
        secondSelect: "",
        isLocked: false,
      });

      setSpecialitiesGroups(transformedData);
    }

    setCreatedTags(projectTagsArray);

    setValues({
      ...values,
      ...projectDetails?.at(0),
      project_status: projectStatus,
      project_attributes:
        projectDetails?.at(0)?.project_attributes?.at(0)?.attribute_value ===
        "true"
          ? 1
          : 0,
      speciality_count: specialityCount,
      project_impressions_target: projectImpressionsTarget,
      project_campaign_groups: projectCampaignGroup || [],
    });
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormBuilder
        fields={fields}
        handleChange={handleChange}
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
      />

      <div>
        <p className="form-label">Choose Campaigns</p>
        <FormikProvider value={formik}>
          <FieldArray name="project_campaign_groups">
            {(prop) => (
              <>
                {values.project_campaign_groups?.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="border border-1 border-primary border-opacity-25 border-dashed rounded-3 mb-4 p-4 px-3"
                    >
                      <Row>
                        <Col lg={12}>
                          <RenderField
                            required={true}
                            type="searchable"
                            placeholder="Select Campaign Group"
                            label={`Select Campaign Group *`}
                            field_name={`project_campaign_groups[${index}].label`}
                            searchText={searchText}
                            loadOptions={debounce((inputText, callback) => {
                              setSearchText(inputText);
                              _loadSuggestions(inputText)
                                .then((options) => {
                                  callback(options);
                                })
                                .catch((err) => {
                                  console.log(err);
                                });
                            }, 300)}
                            handleChange={(val) => {
                              if (val) {
                                setFieldValue(
                                  `project_campaign_groups[${index}].group_id`,
                                  val?.value || ""
                                );
                                setFieldValue(
                                  `project_campaign_groups[${index}].label`,
                                  val?.label || ""
                                );
                              } else {
                                setFieldValue(
                                  `project_campaign_groups[${index}].group_id`,
                                  ""
                                );
                                setFieldValue(
                                  `project_campaign_groups[${index}].label`,
                                  ""
                                );
                              }
                            }}
                            selected={
                              values.project_campaign_groups[index]?.label
                                ? {
                                    label:
                                      values.project_campaign_groups[index]
                                        ?.label,
                                    value:
                                      values.project_campaign_groups[index]
                                        ?.group_id,
                                  }
                                : ""
                            }
                            values={values}
                            touched={touched}
                            errors={errors}
                            setFieldValue={setFieldValue}
                          />
                        </Col>
                        <Col lg={6}>
                          <RenderField
                            type="number"
                            field_name={`project_campaign_groups[${index}].group_impressions_target`}
                            placeholder="Impressions Target"
                            values={values}
                            touched={touched}
                            errors={errors}
                            handleChange={handleChange}
                            label={"Impressions Target"}
                            required={true}
                          />
                        </Col>
                        <Col lg={6}>
                          <RenderField
                            type="number"
                            field_name={`project_campaign_groups[${index}].group_reach_target`}
                            placeholder="Reach Target"
                            values={values}
                            touched={touched}
                            errors={errors}
                            handleChange={handleChange}
                            label={"Reach Target"}
                            required={true}
                          />
                        </Col>
                        <Col
                          lg={12}
                          className="d-flex justify-content-center align-items-center gap-2 flex-wrap"
                        >
                          <Button
                            variant="outline-danger"
                            className="border-dashed flex-shrink-0 flex-sm-grow-0 flex-grow-1 px-md-4 px-3"
                            onClick={() => prop?.remove(index)}
                            disabled={
                              index === 0 &&
                              values?.project_campaign_groups?.length < 2
                                ? true
                                : false
                            }
                          >
                            <span>{findIcon("Trash", "dual-tone", 22)}</span>{" "}
                            Remove
                          </Button>

                          {values?.project_campaign_groups?.length - 1 ===
                            index && (
                            <Button
                              variant={`${
                                values[`project_campaign_groups`][index]
                                  ?.label == ""
                                  ? "outline-secondary"
                                  : "outline-primary"
                              }`}
                              className={
                                "border-dashed flex-shrink-0 flex-sm-grow-0 flex-grow-1 px-4 px-3"
                              }
                              onClick={() =>
                                prop?.push({
                                  group_id: "",
                                  label: "",
                                  group_impressions_target: 0,
                                  group_reach_target: 0,
                                })
                              }
                              disabled={
                                values[`project_campaign_groups`][index]
                                  ?.label == ""
                                  ? true
                                  : false
                              }
                            >
                              <span>{findIcon("Plus", "solid", 22)}</span> Add
                              Row
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </>
            )}
          </FieldArray>
        </FormikProvider>
      </div>

      <div>
        <p className="form-label">Mapped States</p>
        <MappedStates
          stateLists={stateLists}
          dropdownGroups={dropdownGroups}
          setDropdownGroups={setDropdownGroups}
        />
      </div>

      <div>
        <p className="form-label">Mapped Specialities</p>
        <MappedSpecialities
          masterSpecialities={masterSpecialities}
          specialitiesGroups={specialitiesGroups}
          setSpecialitiesGroups={setSpecialitiesGroups}
        />
      </div>

      <div className="d-flex justify-content-end gap-3">
        <Button
          variant="secondary"
          onClick={() => redirectTo("workspace/clirnet")}
        >
          Cancel
        </Button>
        <Button type="submit">{`${
          projectDetails.length > 0 ? "Update" : "Submit"
        }`}</Button>
      </div>
    </Form>
  );
}

export default BrandAdsForm;
